import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { showDiagnostics } from '@/lib/ui/diagnostics';

export interface AnalyticsStatusRowProps {
  /** e.g. { schedule: true, activities: true, risk: true, baseline: false } */
  loaded: Record<string, boolean>;
  errorCount: number;
  /** Raw error messages — only shown when diagnostics flag is enabled. */
  errorMessages?: string[];
  className?: string;
}

/**
 * Minimal baseline & data-quality indicator for analytics pages.
 * Only renders when baseline is missing or errors exist.
 * CTA always links to /app/baselines — never to the new-project upload.
 */
export const AnalyticsStatusRow: React.FC<AnalyticsStatusRowProps> = ({
  loaded,
  errorCount,
  errorMessages,
  className,
}) => {
  const hasBaseline = loaded.baseline === true;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const diagnosticsEnabled = showDiagnostics();

  // Nothing to show — data context is handled by the top navbar
  if (hasBaseline && errorCount === 0) return null;

  return (
    <div
      className={['flex flex-wrap items-center gap-2 text-xs', className].filter(Boolean).join(' ')}
      data-testid="analytics-status-row"
    >
      {!hasBaseline && (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
          No baseline set
          <Button variant="outline" size="sm" asChild className="h-5 text-[10px] px-1.5 ml-0.5">
            <Link to="/app/baselines">Manage baselines</Link>
          </Button>
        </span>
      )}
      {errorCount > 0 && (
        <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-600 dark:bg-amber-400 shrink-0" />
          <span title="Some datasets failed to load. Results may be partial.">
            Data incomplete
          </span>
          {diagnosticsEnabled && errorMessages && errorMessages.length > 0 && (
            <button
              type="button"
              onClick={() => setDetailsOpen((o) => !o)}
              className="text-[10px] underline underline-offset-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {detailsOpen ? 'Hide details' : 'View details'}
            </button>
          )}
        </span>
      )}
      {detailsOpen && diagnosticsEnabled && errorMessages && errorMessages.length > 0 && (
        <div className="basis-full mt-1 rounded border border-border/50 bg-muted/30 p-2 text-[11px] text-muted-foreground space-y-0.5">
          {errorMessages.slice(0, 5).map((msg, i) => (
            <p key={i}>{msg}</p>
          ))}
          {errorMessages.length > 5 && (
            <p className="opacity-60">…and {errorMessages.length - 5} more</p>
          )}
        </div>
      )}
    </div>
  );
};
